import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'form',
    'modalTemplate',
    'sendLink',
    'downloadLink',
    'modal',
    'locale',
    'programLevel',
    'duration',
    'durationsMap',
    'nonStandard',
  ]

  connect() {
    if (this.hasFormTarget) {
      this.formTarget.addEventListener('submit', this.submitForm.bind(this))
    }
    if (!this.hasNonStandardTarget) {
      this.updateProgramLevelAndDurations()

      if (this.hasProgramLevelTarget) {
        this.programLevelTarget.addEventListener('change', () =>
          this.updateDurations(),
        )
      }
      if (this.hasLocaleTarget) {
        this.localeTarget.addEventListener('change', () =>
          this.updateProgramLevelAndDurations(),
        )
      }
    }
  }

  submitForm(e) {
    e.preventDefault()
    const form = this.hasFormTarget ? this.formTarget : e.target
    const formData = new FormData(form)

    fetch(form.action, {
      method: 'POST',
      body: formData,
      headers: { Accept: 'application/json' },
    })
      .then((response) =>
        response
          .json()
          .then((data) => ({ status: response.status, body: data })),
      )
      .then(({ status, body }) => {
        if (status === 422 && body.errors) {
          this.showFlash(body.errors.join('<br>'), 'error')
          return
        } else {
          if (body.redirect_url) {
            window.location.href = body.redirect_url
          } else {
            this.showModal(
              body.download_url,
              body.send_url,
              body.invitation_emailable,
            )
          }
          return
        }
      })
      .catch((error) => {
        this.showFlash('There was an error submitting the form.', error)
      })
  }

  showFlash(message, type = 'error') {
    let flash = this.element.querySelector('#flash-message')
    if (!flash) {
      flash = document.createElement('div')
      flash.id = 'flash-message'
      flash.className = 'mb-4 p-3 rounded'
      // Insert flash directly above the form
      if (this.hasFormTarget && this.formTarget.parentElement) {
        this.formTarget.parentElement.insertBefore(flash, this.formTarget)
      } else {
        this.element.prepend(flash)
      }
    }
    flash.innerHTML = message
    flash.style.display = 'block'
    flash.className =
      'mb-4 p-3 rounded z-50 ' +
      (type === 'error'
        ? 'bg-red-100 text-red-700'
        : 'bg-green-100 text-green-700')
    // Scroll flash into view
    flash.scrollIntoView({ behavior: 'smooth', block: 'center' })
    setTimeout(() => {
      flash.style.display = 'none'
    }, 5000)
  }

  showModal(downloadUrl, sendUrl, invitationEmailable) {
    // Use the modal already in the DOM
    if (!this.hasModalTarget) return
    const modal = this.modalTarget
    const sendLink = this.sendLinkTarget
    const downloadLink = this.downloadLinkTarget

    // Set attributes
    if (sendLink) {
      sendLink.setAttribute('href', sendUrl)
      if (!invitationEmailable) {
        sendLink.setAttribute('disabled', 'disabled')
        sendLink.classList.add('cursor-not-allowed', 'opacity-50')
      } else {
        sendLink.removeAttribute('disabled')
        sendLink.classList.remove('cursor-not-allowed', 'opacity-50')
      }
    }
    if (downloadLink) {
      downloadLink.setAttribute('href', downloadUrl)
    }

    // Show the modal
    modal.showModal()
    modal.addEventListener(
      'close',
      () => {
        window.location.href = '/outplacement/offers'
      },
      { once: true },
    )
  }

  updateDurations() {
    const locale = this.localeTarget.value
    const programLevel = this.programLevelTarget.value
    const key = `${locale}-${programLevel}`
    const durationsMap = JSON.parse(this.durationsMapTarget.value)
    const allowedDurations = durationsMap[key] || []

    // Remove all options except the prompt
    this.durationTarget
      .querySelectorAll("option:not([value=''])")
      .forEach((opt) => opt.remove())

    // Add allowed options
    allowedDurations.forEach((duration) => {
      const option = document.createElement('option')
      option.value = duration
      option.text = `${duration} month${duration > 1 ? 's' : ''}`
      this.durationTarget.appendChild(option)
    })

    if (allowedDurations.length > 0) {
      this.durationTarget.value = allowedDurations[0]
    }
  }

  updateProgramLevelAndDurations() {
    const locale = this.localeTarget.value
    const durationsMap = JSON.parse(this.durationsMapTarget.value)
    const programLevelTitles = {
      TRIAL: 'Trial',
      BRONZE: 'Solo',
      SOLO_PLUS: 'Solo Plus',
      EASE: 'Ease',
      CONNECT: 'Connect',
      SILVER: 'Flex',
      GOLD: 'Premium',
      ADVANCE: 'Advance',
      ESSENTIALS: 'Essentials',
      PROFESSIONAL: 'Professional',
      EXECUTIVE: 'Executive',
      SELECT: 'Select',
      CONCIERGE: 'Concierge',
    }

    const programLevelOrder = [
      'BRONZE',
      'SOLO_PLUS',
      'EASE',
      'SILVER',
      'CONNECT',
      'GOLD',
      'ADVANCE',
      'ESSENTIALS',
      'PROFESSIONAL',
      'EXECUTIVE',
      'SELECT',
      'CONCIERGE',
    ]

    let programLevels = Object.keys(durationsMap)
      .filter((key) => key.startsWith(`${locale}-`))
      .map((key) => key.split('-')[1])

    // Sort programLevels according to the specified order
    programLevels = programLevels.sort((a, b) => {
      const idxA = programLevelOrder.indexOf(a)
      const idxB = programLevelOrder.indexOf(b)
      if (idxA === -1 && idxB === -1) return a.localeCompare(b)
      if (idxA === -1) return 1
      if (idxB === -1) return -1
      return idxA - idxB
    })

    // Remove all options except the prompt
    this.programLevelTarget
      .querySelectorAll("option:not([value=''])")
      .forEach((opt) => opt.remove())

    // Add allowed program levels as options
    programLevels.forEach((level) => {
      const option = document.createElement('option')
      option.value = level
      option.text = programLevelTitles[level] || level
      this.programLevelTarget.appendChild(option)
    })

    // Set the value to the first available program level if any
    if (programLevels.length > 0) {
      this.programLevelTarget.value = programLevels[0]
    } else {
      this.programLevelTarget.value = ''
    }

    this.updateDurations()
  }
}
